import React, { useEffect, useState } from 'react';
import {
    getServiceRecordImageUrl,
    getServiceRecordInvoiceUrl,
} from '../services/vehicleServiceRecordApi';
import { safeNoteHtml } from '../utils/safeHtml';

/**
 * VehicleServiceRecordDetail — drawer body for a single service event.
 *
 * Receives a {@link ServiceRecordDetail} payload from the backend:
 *
 *   {
 *     id, rego, customerName, customerMobile, serviceCompletionDate,
 *     serviceHistory: { [jobNumber]: VehicleServiceJobEntry },
 *     createdAt, updatedAt
 *   }
 *
 * Under the one-row-per-event storage model {@code serviceHistory} contains
 * exactly one entry. This component reads whatever entries are present and
 * renders each as a self-contained job summary — work items, notes, optional
 * loan-car line, optional invoice-PDF link.
 *
 * Pre-signed URLs for note images and invoice PDFs are fetched lazily through
 * the existing api helpers so the snapshot continues to work after the source
 * DB rows have been pruned by the nightly DataCleanupJob.
 */
const VehicleServiceRecordDetail = ({ record }) => {
    if (!record) return null;

    const entries = Object.entries(record.serviceHistory || {});

    if (entries.length === 0) {
        return (
            <div className="text-xs text-slate-400 italic">
                No service detail captured for this record.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {entries.map(([jobNumber, entry]) => (
                <JobEntryCard key={jobNumber} jobNumber={jobNumber} entry={entry} />
            ))}
        </div>
    );
};

// ============================================================================
// JobEntryCard — header summary + actions for one snapshot entry
// ============================================================================

const JobEntryCard = ({ jobNumber, entry }) => {
    const totalCost = computeJobTotal(entry.workItems);

    return (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {/* Header strip */}
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-mono text-xs font-semibold text-slate-500">
                                {jobNumber}
                            </span>
                            <span className="font-semibold text-slate-800 text-sm">
                                {entry.jobDate ? formatDate(entry.jobDate) : '—'}
                            </span>
                            {entry.serviceProfileName && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-100">
                                    {entry.serviceProfileName}
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                            {entry.technician && <span>👤 {entry.technician}</span>}
                            {entry.odometerIn != null && (
                                <span>🚗 {entry.odometerIn.toLocaleString()} km in</span>
                            )}
                            {entry.odometerOut != null && (
                                <span>{entry.odometerOut.toLocaleString()} km out</span>
                            )}
                            <span>
                                {entry.workItems?.length ?? 0} work item
                                {entry.workItems?.length === 1 ? '' : 's'}
                            </span>
                        </div>
                    </div>
                    <span className="text-sm font-bold text-slate-800 shrink-0">
                        {formatCurrency(totalCost)}
                    </span>
                </div>
            </div>

            {/* Body sections */}
            <JobActions entry={entry} />
        </div>
    );
};

// ============================================================================
// JobActions — work items, notes, loan car, invoice button
// ============================================================================

const JobActions = ({ entry }) => {
    const [invoiceLoading, setInvoiceLoading] = useState(false);

    const handleInvoiceClick = async () => {
        if (!entry.invoicePdfS3Key || invoiceLoading) return;
        setInvoiceLoading(true);
        try {
            const res = await getServiceRecordInvoiceUrl(entry.invoicePdfS3Key);
            window.open(res.data.url, '_blank', 'noopener');
        } catch {
            // swallow
        } finally {
            setInvoiceLoading(false);
        }
    };

    return (
        <div className="p-4 space-y-3">
            {entry.loanCarDetails && (
                <div className="text-xs">
                    <span className="font-semibold text-slate-500">Loan car:</span>{' '}
                    <span className="text-slate-700">{entry.loanCarDetails}</span>
                </div>
            )}

            {entry.workItems?.length > 0 && (
                <div>
                    <SectionLabel>Work performed</SectionLabel>
                    <div className="space-y-2">
                        {entry.workItems.map((wi, i) => (
                            <WorkItemRow key={i} workItem={wi} />
                        ))}
                    </div>
                </div>
            )}

            {entry.notes?.length > 0 && (
                <div>
                    <SectionLabel>Notes</SectionLabel>
                    <div className="space-y-2">
                        {entry.notes.map((n, i) => (
                            <NoteRow key={i} note={n} />
                        ))}
                    </div>
                </div>
            )}

            {entry.invoicePdfS3Key && (
                <div className="pt-2 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={handleInvoiceClick}
                        disabled={invoiceLoading}
                        className="text-sm text-emerald-700 hover:text-emerald-800 font-medium underline-offset-2 hover:underline disabled:opacity-50"
                    >
                        {invoiceLoading ? 'Opening…' : '📄 View Invoice PDF'}
                    </button>
                </div>
            )}
        </div>
    );
};

// ============================================================================
// ObservationPairRow — single row showing one measurement's IN and OUT side-by-side
// ============================================================================
//
// The 3-column grid mirrors the column header in WorkItemRow's measurements block:
// [ measurement label | IN cell | OUT cell ]. Each phase cell renders its own
// value + status badge, colored independently — so a measurement that started
// AMBER and ended GREEN reads as "amber on the left, green on the right" without
// any extra cognitive load.
//
// A missing phase entry (shouldn't happen given Policy 1 + lockstep link/unlink,
// but defensive against future drift) renders as a muted dash so the columns
// stay aligned and the gap is visible rather than silent.

const ObservationPairRow = ({ pair, showName = true, showPositionLabel = true }) => {
    // Whether the left cell will actually contain anything to show.
    const hasNameLine = showName && !!pair.name;
    const hasPositionChip = showPositionLabel && !!pair.positionLabel;
    const hasSubChip = !!pair.subPositionLabel;

    return (
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-1.5 items-center">
            {/* Label cell — composition depends on context:
                  - Flat list (default):           name + position chip + sub-position chip
                  - Inside a position group:       sub-position chip only (showName=false,
                                                   showPositionLabel=false)
                  - Single-slot under name:        empty cell (no chips at all)
                The renderer caller picks the right combination so this component
                stays generic and the higher-level layout decides what context the
                row appears in. */}
            <div className="px-2 py-1 text-xs">
                {hasNameLine && (
                    <div className="font-semibold text-slate-700 truncate">{pair.name}</div>
                )}
                {(hasPositionChip || hasSubChip) && (
                    <div className={`flex items-center gap-1 flex-wrap ${hasNameLine ? 'mt-0.5' : ''}`}>
                        {hasPositionChip && (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wide">
                                {pair.positionLabel}
                            </span>
                        )}
                        {hasSubChip && (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 text-[10px] font-bold uppercase tracking-wide">
                                {pair.subPositionLabel}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <ObservationPhaseCell observation={pair.in} />
            <ObservationPhaseCell observation={pair.out} />
        </div>
    );
};

// Single-phase cell — value + status pill. `observation` may be null if the
// phase wasn't recorded (rare; rendered as a muted placeholder).
const ObservationPhaseCell = ({ observation }) => {
    if (!observation) {
        return (
            <div className="px-2 py-1 rounded text-xs border border-dashed border-slate-200 bg-slate-50/50 text-slate-300 italic">
                — not recorded
            </div>
        );
    }

    const status = observation.observationStatus || 'UNCLASSIFIED';
    const cls = STATUS_STYLES[status] || STATUS_STYLES.UNCLASSIFIED;

    return (
        <div className={`flex items-center justify-between gap-2 px-2 py-1 rounded text-xs border ${cls}`}>
            <span className="truncate">{observation.value || '—'}</span>
            <span className="font-mono text-[10px] uppercase tracking-wider shrink-0">
                {status}
            </span>
        </div>
    );
};

// ============================================================================
// WorkItemRow
// ============================================================================

const STATUS_STYLES = {
    GREEN:        'bg-emerald-50 text-emerald-800 border-emerald-200',
    AMBER:        'bg-amber-50 text-amber-800 border-amber-200',
    RED:          'bg-red-50 text-red-800 border-red-200',
    UNCLASSIFIED: 'bg-slate-50 text-slate-600 border-slate-200',
};

const OUTCOME_STYLES = {
    COMPLETED: 'bg-emerald-100 text-emerald-700',
    DECLINED:  'bg-red-100 text-red-700',
    SKIPPED:   'bg-slate-100 text-slate-600',
};

/**
 * Authorisation box styling, keyed on `customerDecision`.
 *
 * UNKNOWN covers snapshots taken before that field existed — they carry a
 * `customerConfirmation` string of a different shape and no decision, so they keep the
 * neutral treatment rather than being guessed at. A service record is evidence; the
 * screen must not colour an old row green on an assumption.
 */
const DECISION_STYLES = {
    APPROVED:     'bg-emerald-50 border-emerald-200 text-emerald-900',
    DECLINED:     'bg-red-50 border-red-200 text-red-900',
    AWAITING:     'bg-amber-50 border-amber-200 text-amber-900',
    NOT_REQUIRED: 'bg-slate-50 border-slate-200 text-slate-700',
    UNKNOWN:      'bg-blue-50 border-blue-100 text-blue-900',
};

const WorkItemRow = ({ workItem }) => {
    const {
        workItemName,
        outcome,
        costDetail,
        measurementsObserved,
        customerConfirmation,
        customerDecision,
        workItemStaffComment,
    } = workItem;

    const cost = computeWorkItemCost(workItem);
    const outcomeCls = OUTCOME_STYLES[outcome] || 'bg-slate-100 text-slate-600';

    return (
        <div className="border border-slate-200 rounded-lg p-3 bg-white">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-800 text-sm">{workItemName}</div>
                    {outcome && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${outcomeCls}`}>
                            {outcome}
                        </span>
                    )}
                </div>
                <div className="text-sm font-semibold text-slate-700 shrink-0">
                    {formatCurrency(cost)}
                </div>
            </div>

            {costDetail && (costDetail.labour || costDetail.parts?.length > 0) && (
                <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                    {costDetail.labour && (
                        <div className="flex justify-between">
                            <span>Labour: {costDetail.labour.description || '—'}</span>
                            <span className="font-mono">{formatCurrency(costDetail.labour.value)}</span>
                        </div>
                    )}
                    {costDetail.parts?.map((p, i) => (
                        <div key={i} className="flex justify-between">
                            <span>
                                Part: {p.description}
                                {p.partNo && p.partNo !== 'N/A' && (
                                    <span className="text-slate-400 ml-1">({p.partNo})</span>
                                )}
                            </span>
                            <span className="font-mono">{formatCurrency(p.value)}</span>
                        </div>
                    ))}
                </div>
            )}

            {(measurementsObserved?.length > 0 || workItem.measurementsObservedOut?.length > 0) && (
                <div className="mt-2 space-y-2">
                    {/* Column header — only shown when there's something to render. Keeps the
                        rows below from looking like a free-floating mystery grid. */}
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-1.5 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        <div>Measurement</div>
                        <div>Before (IN)</div>
                        <div>After (OUT)</div>
                    </div>

                    {(() => {
                        // Render observations as a NESTED hierarchy so the customer-facing
                        // report stays readable for sub-positioned measurements (e.g. tyre
                        // tread depth has 12 cells = 4 positions × 3 sub-positions):
                        //
                        //   Tyre tread depth                  ← measurement name (once)
                        //     LF                              ← position group header
                        //       OUTER   IN  OUT
                        //       MIDDLE  IN  OUT
                        //       INNER   IN  OUT
                        //     RF
                        //       OUTER   IN  OUT
                        //       ...
                        //
                        // A single-slot measurement (no positions) renders as a single row
                        // under the measurement name with no group header — the grouping
                        // collapses naturally when there's nothing to group by.
                        const pairs = pairObservations(measurementsObserved, workItem.measurementsObservedOut);
                        const groups = groupPairsByPosition(pairs);

                        return groups.map((measurementGroup) => (
                            <div key={measurementGroup.measurementUuid} className="space-y-1">
                                {/* Measurement name — once per measurement, above all its
                                    position groups. Skipped for "" placeholder measurements
                                    (defensive — every real observation has a name). */}
                                {measurementGroup.name && (
                                    <div className="px-2 pt-0.5 text-xs font-semibold text-slate-700">
                                        {measurementGroup.name}
                                    </div>
                                )}

                                {measurementGroup.positions.map((positionGroup) => (
                                    <div key={`${measurementGroup.measurementUuid}::${positionGroup.positionLabel ?? '_'}`}>
                                        {/* Position header — only when there's an actual position
                                            label. Single-slot measurements skip this entirely
                                            and their one row sits directly under the name. */}
                                        {positionGroup.positionLabel && (
                                            <div className="px-2 py-0.5 flex items-center gap-1.5">
                                                <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wide">
                                                    {positionGroup.positionLabel}
                                                </span>
                                                {/* Subtle row count chip when there are multiple
                                                    sub-cells — gives a quick scan hint without
                                                    needing to count rows. */}
                                                {positionGroup.pairs.length > 1 && (
                                                    <span className="text-[10px] text-slate-400">
                                                        {positionGroup.pairs.length} readings
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Sub-position rows within this position group. Each
                                            row shows ONLY the sub-position chip on the left
                                            (since the position is already in the header above)
                                            plus the IN/OUT phase cells on the right. */}
                                        {positionGroup.pairs.map((pair, i) => (
                                            <ObservationPairRow
                                                key={`${pair.measurementUuid}::${pair.positionLabel ?? ''}::${pair.subPositionLabel ?? ''}::${i}`}
                                                pair={pair}
                                                showName={false}
                                                showPositionLabel={false}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ));
                    })()}
                </div>
            )}

            {customerConfirmation && (
                /* Coloured by the DECISION, not by the outcome of the work. Someone
                   defending an invoice months later scans for the red lines first —
                   "you did work I refused" is the question this box answers, and a
                   uniform blue makes them read every item to find out. */
                <div className={`mt-2 p-2 border rounded text-xs ${DECISION_STYLES[customerDecision] || DECISION_STYLES.UNKNOWN}`}>
                    <span className="font-semibold">Customer:</span> {customerConfirmation}
                </div>
            )}

            {workItemStaffComment && (
                <div className="mt-2 p-2 bg-slate-50 border border-slate-100 rounded text-xs text-slate-700">
                    <span className="font-semibold">Staff:</span> {workItemStaffComment}
                </div>
            )}
        </div>
    );
};

// ============================================================================
// NoteRow + NoteImage
// ============================================================================

const NoteRow = ({ note }) => (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
        <div className="flex items-baseline justify-between gap-2 text-xs text-slate-500 mb-1.5">
            <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-700">{note.authorName || '—'}</span>
                {note.linkedWorkItemName && (
                    <span className="text-slate-500">→ {note.linkedWorkItemName}</span>
                )}
            </div>
            <span>{note.createdAt ? formatDate(note.createdAt) : ''}</span>
        </div>
        <div
            className="text-sm text-slate-700 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={safeNoteHtml(note.noteText)}
        />
        {note.images?.length > 0 && (
            <div className="mt-2 flex gap-2 flex-wrap">
                {note.images.map((img) => (
                    <NoteImage key={img.imageUuid || img.s3Key} image={img} />
                ))}
            </div>
        )}
    </div>
);

const NoteImage = ({ image }) => {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!image.s3Key) return;
        let cancelled = false;
        setLoading(true);
        getServiceRecordImageUrl(image.s3Key)
            .then((res) => { if (!cancelled) setUrl(res.data.url); })
            .catch(() => { if (!cancelled) setUrl(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [image.s3Key]);

    if (loading) {
        return <div className="w-20 h-20 rounded border border-slate-200 bg-slate-100 animate-pulse" />;
    }
    if (!url) {
        return (
            <div className="w-20 h-20 rounded border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-400">
                ?
            </div>
        );
    }
    return (
        <a href={url} target="_blank" rel="noopener noreferrer">
            <img
                src={url}
                alt={image.originalFilename || 'note image'}
                className="w-20 h-20 object-cover rounded border border-slate-200 hover:border-slate-400 transition-colors"
            />
        </a>
    );
};

// ============================================================================
// Atoms
// ============================================================================

const SectionLabel = ({ children }) => (
    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
        {children}
    </div>
);

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(); }
    catch { return '—'; }
};

const formatCurrency = (n) => {
    if (n == null || isNaN(n)) return '—';
    return `$${Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
};

const computeWorkItemCost = (wi) => {
    if (!wi?.costDetail) return 0;
    let total = 0;
    if (wi.costDetail.labour?.value != null) total += Number(wi.costDetail.labour.value) || 0;
    if (Array.isArray(wi.costDetail.parts)) {
        total += wi.costDetail.parts.reduce((sum, p) => sum + (Number(p?.value) || 0), 0);
    }
    return total;
};

const computeJobTotal = (workItems) => {
    if (!Array.isArray(workItems)) return 0;
    return workItems.reduce((sum, wi) => sum + computeWorkItemCost(wi), 0);
};

/**
 * Pair IN and OUT observation lists by composite key
 * (measurementUuid, positionLabel, subPositionLabel).
 *
 * Iterates the IN list first to preserve the order the technician originally
 * recorded measurements; OUT entries are looked up by composite key and attached
 * to their matching IN row. Any OUT entries with no IN counterpart are appended
 * at the end with a null `in` field, so nothing gets silently dropped — those
 * shouldn't exist in normal operation but we surface them rather than hide them.
 *
 * The triple key is required for sub-positioned measurements (e.g. tyre tread
 * depth at LF×{OUTER,MIDDLE,INNER}): without subPositionLabel in the key, all
 * three LF rows would collide on `measurementUuid::LF` and only one would
 * survive. With it, each sub-cell gets its own pair, and the IN-list ordering
 * naturally groups them (LF OUTER, LF MIDDLE, LF INNER, RF OUTER, ...).
 *
 * Returns an array of:
 *   { measurementUuid, name, positionLabel, subPositionLabel, in, out }
 *
 * where `in` and `out` are the raw observation objects (or null if missing).
 * Snapshotted metadata (name, position label, sub-position label) is taken
 * from whichever side is present — they snapshot the same Measurement
 * definition so it's identical between the two phases by construction.
 */
const pairObservations = (inList, outList) => {
    const safeIn  = Array.isArray(inList)  ? inList  : [];
    const safeOut = Array.isArray(outList) ? outList : [];

    const keyOf = (obs) =>
        `${obs.measurementUuid}::${obs.positionLabel ?? ''}::${obs.subPositionLabel ?? ''}`;

    const outByKey = new Map();
    for (const obs of safeOut) outByKey.set(keyOf(obs), obs);

    const pairs = [];
    const consumedKeys = new Set();

    for (const inObs of safeIn) {
        const key = keyOf(inObs);
        const outObs = outByKey.get(key) || null;
        if (outObs) consumedKeys.add(key);
        pairs.push({
            measurementUuid:  inObs.measurementUuid,
            name:             inObs.name,
            positionLabel:    inObs.positionLabel,
            subPositionLabel: inObs.subPositionLabel,
            in:               inObs,
            out:              outObs,
        });
    }

    // Surface any OUT-only entries (no IN counterpart) — defensive; shouldn't happen
    for (const outObs of safeOut) {
        const key = keyOf(outObs);
        if (consumedKeys.has(key)) continue;
        pairs.push({
            measurementUuid:  outObs.measurementUuid,
            name:             outObs.name,
            positionLabel:    outObs.positionLabel,
            subPositionLabel: outObs.subPositionLabel,
            in:               null,
            out:              outObs,
        });
    }

    return pairs;
};

/**
 * Reshape a flat paired list into a nested hierarchy:
 *
 *   [
 *     {
 *       measurementUuid, name,
 *       positions: [
 *         { positionLabel, pairs: [pair, pair, ...] },
 *         ...
 *       ]
 *     },
 *     ...
 *   ]
 *
 * Used by the renderer to display observations grouped first by measurement
 * (one name header at the top) and then by position (one chip header per
 * corner / side), with the actual sub-position rows underneath each position.
 *
 * For single-slot measurements (no positionLabel and no subPositionLabel) the
 * shape still works — the measurement gets one position group with
 * {@code positionLabel = null} containing one pair. The renderer hides the
 * position header when {@code positionLabel} is falsy, so the row sits
 * directly under the measurement name with no decorative grouping.
 *
 * Ordering is preserved from the input list: the first-seen order of each
 * measurementUuid wins for measurements, and within a measurement the first-
 * seen order of each positionLabel wins for positions. This matches the
 * IN-list insertion order which itself matches the technician's recording
 * sequence — so the report reads top-to-bottom in the order things happened.
 */
const groupPairsByPosition = (pairs) => {
    const measurementMap = new Map(); // measurementUuid -> { measurementUuid, name, positionMap }
    for (const pair of pairs) {
        let mEntry = measurementMap.get(pair.measurementUuid);
        if (!mEntry) {
            mEntry = {
                measurementUuid: pair.measurementUuid,
                name:            pair.name,
                positionMap:     new Map(), // positionLabel (or null) -> { positionLabel, pairs: [] }
            };
            measurementMap.set(pair.measurementUuid, mEntry);
        }
        const posKey = pair.positionLabel ?? '';
        let pEntry = mEntry.positionMap.get(posKey);
        if (!pEntry) {
            pEntry = { positionLabel: pair.positionLabel, pairs: [] };
            mEntry.positionMap.set(posKey, pEntry);
        }
        pEntry.pairs.push(pair);
    }

    // Materialise to plain arrays in insertion order.
    return Array.from(measurementMap.values()).map((m) => ({
        measurementUuid: m.measurementUuid,
        name:            m.name,
        positions:       Array.from(m.positionMap.values()),
    }));
};

export default VehicleServiceRecordDetail;